/* ══════════════════════════════════════════════════════════════════════════
   kr-press-core.js — KR Maps Press · core runtime
   window.KRPress: config, event bus, module registry, state store + autosave,
   IndexedDB wrapper, EXIF parser, thumbnailer, offline place index, utils and
   the boot/module-mount router.

   Contract: scratchpad/ARCHITECTURE.md §"Core public API". UI modules code
   against exactly that surface. Pure logic (exif, place math, util, filename
   heuristics) is defined first with no DOM/window references so a Node test
   shim (`global.window = {}`) can load this file and exercise it directly.
   Vanilla ES2019 · IIFE · no dependencies.
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ════════════════════════ 1 · util (pure) ════════════════════════ */

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function haversineKm(lat1, lon1, lat2, lon2) {
    var R = 6371, r = Math.PI / 180;
    var dLat = (lat2 - lat1) * r, dLon = (lon2 - lon1) * r;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * r) * Math.cos(lat2 * r) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return 2 * R * Math.asin(Math.sqrt(Math.min(1, a)));
  }

  /* Wall-clock rule (ARCHITECTURE §Hard constraints #7): a photo's own
     wall-clock time is displayed as-is, never UTC-shifted. Metas carry the
     original EXIF components in `wall {y,mo,d,h,mi,s}`; when absent we fall
     back to interpreting takenAt in the viewer's local zone (which is exactly
     how takenAt was derived when no EXIF offset was known — so the roundtrip
     is stable). */
  function wallOf(meta) {
    if (!meta) return null;
    if (meta.wall && typeof meta.wall.y === 'number') return meta.wall;
    if (typeof meta.takenAt === 'number') {
      var d = new Date(meta.takenAt);
      return { y: d.getFullYear(), mo: d.getMonth() + 1, d: d.getDate(),
               h: d.getHours(), mi: d.getMinutes(), s: d.getSeconds() };
    }
    return null;
  }

  function fmtDay(meta) {                        // → 'Aug 21'
    var w = wallOf(meta);
    return w ? MONTHS[w.mo - 1] + ' ' + w.d : '';
  }

  function fmtTime(meta) {                       // → '2:06 PM'
    var w = wallOf(meta);
    if (!w) return '';
    var h12 = w.h % 12 || 12;
    return h12 + ':' + String(w.mi).padStart(2, '0') + (w.h < 12 ? ' AM' : ' PM');
  }

  function fmtDateRange(metaA, metaB) {          // → 'AUG 3 – 23, 2025'
    var a = wallOf(metaA), b = wallOf(metaB);
    if (!a && !b) return '';
    if (!a) a = b;
    if (!b) b = a;
    var MA = MONTHS[a.mo - 1].toUpperCase(), MB = MONTHS[b.mo - 1].toUpperCase();
    if (a.y === b.y && a.mo === b.mo && a.d === b.d) return MA + ' ' + a.d + ', ' + a.y;
    if (a.y === b.y && a.mo === b.mo) return MA + ' ' + a.d + ' – ' + b.d + ', ' + a.y;
    if (a.y === b.y) return MA + ' ' + a.d + ' – ' + MB + ' ' + b.d + ', ' + a.y;
    return MA + ' ' + a.d + ', ' + a.y + ' – ' + MB + ' ' + b.d + ', ' + b.y;
  }

  function debounce(fn, ms) {
    var t = null, lastArgs = null, lastThis = null;
    function run() { t = null; var a = lastArgs; lastArgs = lastThis = null; fn.apply(null, a || []); }
    function debounced() {
      lastArgs = arguments; lastThis = this;
      if (t) clearTimeout(t);
      t = setTimeout(run, ms);
    }
    debounced.cancel = function () { if (t) clearTimeout(t); t = null; lastArgs = null; };
    debounced.flush = function () { if (t) { clearTimeout(t); run(); } };
    return debounced;
  }

  function uid(prefix) {
    return (prefix || 'id') + '_' + Date.now().toString(36) +
           Math.random().toString(36).slice(2, 8);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function delay(ms) { return new Promise(function (res) { setTimeout(res, ms); }); }

  function deepClone(obj) {
    if (typeof structuredClone === 'function') {
      try { return structuredClone(obj); } catch (e) { /* fall through */ }
    }
    return JSON.parse(JSON.stringify(obj));
  }

  /* ════════════════════════ 2 · EXIF parser (pure) ════════════════════════
     Supports: JPEG APP1, HEIC/HEIF (ISOBMFF meta→iinf→iloc), PNG eXIf chunk,
     WebP RIFF EXIF chunk, bare TIFF (incl. TIFF-based RAW like DNG/NEF/ARW).
     parse() never throws on malformed data — best-effort partial or null.
     All loops are capped (64 IFD entries, 48 boxes wide, depth 8) so corrupt
     length fields can't spin. */

  // TIFF field type → byte size. 1 BYTE, 2 ASCII, 3 SHORT, 4 LONG,
  // 5 RATIONAL, 6 SBYTE, 7 UNDEFINED, 8 SSHORT, 9 SLONG, 10 SRATIONAL.
  var TYPE_SIZE = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 6: 1, 7: 1, 8: 2, 9: 4, 10: 8 };
  var MAX_IFD_ENTRIES = 64;
  var MAX_BOXES = 48;

  function fourcc(view, off) {
    if (off < 0 || off + 4 > view.byteLength) return '';
    return String.fromCharCode(view.getUint8(off), view.getUint8(off + 1),
                               view.getUint8(off + 2), view.getUint8(off + 3));
  }

  function isTiffAt(view, off) {
    if (off < 0 || off + 8 > view.byteLength) return false;
    var b = view.getUint16(off, false);
    if (b !== 0x4949 && b !== 0x4D4D) return false;       // 'II' / 'MM'
    return view.getUint16(off + 2, b === 0x4949) === 42;  // TIFF magic
  }

  // Read one IFD's entry table. Values ≤4 bytes are stored inline,
  // LEFT-justified in the 4-byte value field (TIFF6 §2), so the inline
  // position is independent of endianness; larger values live at an offset
  // relative to the TIFF header.
  function ifdEntries(view, tiffStart, ifdOff, le) {
    var abs = tiffStart + ifdOff;
    if (ifdOff <= 0 || abs + 2 > view.byteLength) return { entries: [], next: 0 };
    var nRaw = view.getUint16(abs, le);
    var n = Math.min(nRaw, MAX_IFD_ENTRIES);
    var out = [];
    for (var i = 0; i < n; i++) {
      var e = abs + 2 + i * 12;
      if (e + 12 > view.byteLength) break;
      var tag = view.getUint16(e, le);
      var type = view.getUint16(e + 2, le);
      var count = view.getUint32(e + 4, le);
      var size = (TYPE_SIZE[type] || 1) * count;
      var at = size <= 4 ? e + 8 : tiffStart + view.getUint32(e + 8, le);
      out.push({ tag: tag, type: type, count: count, at: at });
    }
    var nextAt = abs + 2 + nRaw * 12;
    var next = (nextAt >= 0 && nextAt + 4 <= view.byteLength) ? view.getUint32(nextAt, le) : 0;
    return { entries: out, next: next };
  }

  function readAscii(view, e) {
    var s = '';
    for (var i = 0; i < e.count && i < 256; i++) {
      if (e.at + i >= view.byteLength) break;
      var c = view.getUint8(e.at + i);
      if (c === 0) break;
      s += String.fromCharCode(c);
    }
    return s.trim();
  }

  function readUint(view, e, le, idx) {
    idx = idx || 0;
    if (e.type === 3) return view.getUint16(e.at + idx * 2, le);
    if (e.type === 4) return view.getUint32(e.at + idx * 4, le);
    if (e.type === 1 || e.type === 7) return view.getUint8(e.at + idx);
    return null;
  }

  function readRational(view, e, le, idx) {
    var o = e.at + (idx || 0) * 8;
    var num, den;
    if (e.type === 10) { num = view.getInt32(o, le); den = view.getInt32(o + 4, le); }
    else { num = view.getUint32(o, le); den = view.getUint32(o + 4, le); }
    return den ? num / den : 0;
  }

  // GPS coordinate: up to 3 rationals (deg, min, sec) + hemisphere ref.
  function readGpsCoord(view, e, le, ref) {
    var div = [1, 60, 3600], val = 0;
    var n = Math.min(e.count, 3);
    for (var i = 0; i < n; i++) val += readRational(view, e, le, i) / div[i];
    if (ref === 'S' || ref === 'W') val = -val;
    return val;
  }

  // 'YYYY:MM:DD HH:MM:SS' (+ optional subsec digits, + optional '±HH:MM'
  // offset). With an offset the instant is exact; without one we interpret
  // the wall clock as LOCAL time — never as UTC — so day grouping matches
  // what the camera showed the traveller.
  function buildTimestamp(dto, offsetStr, subsec) {
    var m = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(dto || '');
    if (!m) return null;
    var y = +m[1], mo = +m[2], d = +m[3], h = +m[4], mi = +m[5], s = +m[6];
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || s > 60) return null;
    var ms = 0;
    if (subsec) {
      var digits = String(subsec).replace(/\D/g, '').slice(0, 3);
      if (digits) ms = parseInt(digits.padEnd(3, '0'), 10);
    }
    var tzOffsetMin = null, takenAt;
    var om = /^([+-])(\d{2}):?(\d{2})/.exec(offsetStr || '');
    if (om) tzOffsetMin = (om[1] === '-' ? -1 : 1) * (+om[2] * 60 + +om[3]);
    else if (/^Z/.test(offsetStr || '')) tzOffsetMin = 0;
    if (tzOffsetMin !== null) {
      takenAt = Date.UTC(y, mo - 1, d, h, mi, s, ms) - tzOffsetMin * 60000;
    } else {
      takenAt = new Date(y, mo - 1, d, h, mi, s, ms).getTime();
    }
    return { takenAt: takenAt, tzOffsetMin: tzOffsetMin,
             wall: { y: y, mo: mo, d: d, h: h, mi: mi, s: s } };
  }

  // Parse a TIFF block (IFD0 → ExifIFD / GPS IFD / IFD1 thumbnail).
  // Each section is independently guarded so one corrupt IFD cannot take
  // down the fields we already read.
  function parseTiffBlock(view, tiffStart) {
    if (!isTiffAt(view, tiffStart)) return null;
    var le = view.getUint16(tiffStart, false) === 0x4949;
    var r = { takenAt: null, tzOffsetMin: null, lat: null, lon: null, alt: null,
              camera: null, orientation: 1, w: null, h: null, thumb: null, wall: null };
    var exifPtr = null, gpsPtr = null, ifd1Off = 0;
    var i, e;

    try {
      var ifd0 = ifdEntries(view, tiffStart, view.getUint32(tiffStart + 4, le), le);
      ifd1Off = ifd0.next;
      var dtoFallback = null;
      for (i = 0; i < ifd0.entries.length; i++) {
        e = ifd0.entries[i];
        if (e.tag === 0x0110) r.camera = readAscii(view, e) || null;            // Model
        else if (e.tag === 0x0112) {                                            // Orientation
          var o = readUint(view, e, le);
          if (o >= 1 && o <= 8) r.orientation = o;
        }
        else if (e.tag === 0x8769) exifPtr = readUint(view, e, le);             // Exif IFD ptr
        else if (e.tag === 0x8825) gpsPtr = readUint(view, e, le);              // GPS IFD ptr
        else if (e.tag === 0x0132) dtoFallback = readAscii(view, e);            // DateTime (fallback)
      }
    } catch (err) { /* corrupt IFD0 — keep whatever we have */ }

    try {
      if (exifPtr) {
        var exif = ifdEntries(view, tiffStart, exifPtr, le);
        var dto = null, offsetStr = null, subsec = null;
        for (i = 0; i < exif.entries.length; i++) {
          e = exif.entries[i];
          if (e.tag === 0x9003) dto = readAscii(view, e);                       // DateTimeOriginal
          else if (e.tag === 0x9011) offsetStr = readAscii(view, e);            // OffsetTimeOriginal
          else if (e.tag === 0x9291) subsec = readAscii(view, e);               // SubSecTimeOriginal
          else if (e.tag === 0xA002) r.w = readUint(view, e, le);               // PixelXDimension
          else if (e.tag === 0xA003) r.h = readUint(view, e, le);               // PixelYDimension
        }
        var ts = buildTimestamp(dto || dtoFallback, offsetStr, subsec);
        if (ts) { r.takenAt = ts.takenAt; r.tzOffsetMin = ts.tzOffsetMin; r.wall = ts.wall; }
      } else if (typeof dtoFallback === 'string') {
        var ts2 = buildTimestamp(dtoFallback, null, null);
        if (ts2) { r.takenAt = ts2.takenAt; r.tzOffsetMin = ts2.tzOffsetMin; r.wall = ts2.wall; }
      }
    } catch (err) { /* corrupt Exif IFD */ }

    try {
      if (gpsPtr) {
        var gps = ifdEntries(view, tiffStart, gpsPtr, le);
        var latE = null, lonE = null, altE = null, latRef = '', lonRef = '', altRef = 0;
        for (i = 0; i < gps.entries.length; i++) {
          e = gps.entries[i];
          if (e.tag === 0x0001) latRef = readAscii(view, e);
          else if (e.tag === 0x0002) latE = e;
          else if (e.tag === 0x0003) lonRef = readAscii(view, e);
          else if (e.tag === 0x0004) lonE = e;
          else if (e.tag === 0x0005) altRef = readUint(view, e, le) || 0;
          else if (e.tag === 0x0006) altE = e;
        }
        if (latE && lonE) {
          var lat = readGpsCoord(view, latE, le, latRef);
          var lon = readGpsCoord(view, lonE, le, lonRef);
          // (0,0) is the "no fix" sentinel some cameras write — treat as absent.
          if (Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && !(lat === 0 && lon === 0)) {
            r.lat = lat; r.lon = lon;
          }
        }
        if (altE && r.lat !== null) {
          var alt = readRational(view, altE, le);
          r.alt = altRef === 1 ? -alt : alt;   // ref 1 = below sea level
        }
      }
    } catch (err) { /* corrupt GPS IFD */ }

    try {
      // IFD1 = thumbnail directory. 0x0201 JPEGInterchangeFormat (offset from
      // TIFF header), 0x0202 …Length. Copy the bytes out (slice) so the tiny
      // thumb doesn't pin the multi-MB original buffer in memory.
      if (ifd1Off) {
        var ifd1 = ifdEntries(view, tiffStart, ifd1Off, le);
        var tOff = null, tLen = null;
        for (i = 0; i < ifd1.entries.length; i++) {
          e = ifd1.entries[i];
          if (e.tag === 0x0201) tOff = readUint(view, e, le);
          else if (e.tag === 0x0202) tLen = readUint(view, e, le);
        }
        if (tOff && tLen && tLen > 0 && tLen < 1048576 &&
            tiffStart + tOff + tLen <= view.byteLength) {
          r.thumb = new Uint8Array(view.buffer, view.byteOffset + tiffStart + tOff, tLen).slice();
        }
      }
    } catch (err) { /* corrupt IFD1 */ }

    return r;
  }

  // JPEG: walk marker segments until SOS/EOI, looking for APP1 'Exif\0\0'.
  function findTiffInJpeg(view) {
    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xFFD8) return -1;
    var off = 2, guard = 0;
    while (off + 4 <= view.byteLength && guard++ < MAX_BOXES + 16) {
      if (view.getUint8(off) !== 0xFF) { off++; continue; }
      var marker = view.getUint8(off + 1);
      if (marker === 0xFF) { off++; continue; }                       // fill byte
      if (marker === 0xD8 || marker === 0x01 ||
          (marker >= 0xD0 && marker <= 0xD7)) { off += 2; continue; } // standalone
      if (marker === 0xDA || marker === 0xD9) break;                  // image data / EOI
      var len = view.getUint16(off + 2, false);
      if (len < 2) break;
      if (marker === 0xE1 && len >= 10 &&
          view.getUint32(off + 4, false) === 0x45786966 &&            // 'Exif'
          view.getUint16(off + 8, false) === 0) {
        return off + 10;                                              // TIFF header
      }
      off += 2 + len;
    }
    return -1;
  }

  // PNG: [len u32][type 4cc][data][crc]; the eXIf chunk data IS a TIFF block.
  function findTiffInPng(view) {
    if (view.byteLength < 16 ||
        view.getUint32(0, false) !== 0x89504E47 ||
        view.getUint32(4, false) !== 0x0D0A1A0A) return -1;
    var off = 8, guard = 0;
    while (off + 8 <= view.byteLength && guard++ < MAX_BOXES + 16) {
      var clen = view.getUint32(off, false);
      var type = view.getUint32(off + 4, false);
      if (type === 0x65584966) return off + 8;                        // 'eXIf'
      if (type === 0x49454E44) break;                                 // 'IEND'
      if (clen > view.byteLength) break;
      off += 12 + clen;                                               // len+type+crc
    }
    return -1;
  }

  // WebP: RIFF container, chunk sizes little-endian, padded to even length.
  // The 'EXIF' chunk may or may not carry the JPEG-style 'Exif\0\0' prefix.
  function findTiffInWebp(view) {
    if (fourcc(view, 0) !== 'RIFF' || fourcc(view, 8) !== 'WEBP') return -1;
    var off = 12, guard = 0;
    while (off + 8 <= view.byteLength && guard++ < MAX_BOXES) {
      var tag = fourcc(view, off);
      var size = view.getUint32(off + 4, true);
      if (tag === 'EXIF') {
        var p = off + 8;
        if (fourcc(view, p) === 'Exif' && p + 6 <= view.byteLength &&
            view.getUint16(p + 4, false) === 0) p += 6;
        return p;
      }
      if (size > view.byteLength) break;
      off += 8 + size + (size & 1);
    }
    return -1;
  }

  // ISOBMFF box iterator: cb(type, payloadStart, payloadEnd). Handles 32-bit,
  // 64-bit (size==1) and to-end (size==0) box sizes; caps width and depth.
  function eachBox(view, start, end, cb, depth) {
    if ((depth || 0) > 8) return;
    var off = start, count = 0;
    while (off + 8 <= end && count++ < MAX_BOXES) {
      var size = view.getUint32(off, false);
      var type = fourcc(view, off + 4);
      var hdr = 8;
      if (size === 1) {
        if (off + 16 > end) return;
        // JS can't do 64-bit ints without BigInt; hi*2^32+lo is exact for
        // any realistic file size (< 2^53).
        size = view.getUint32(off + 8, false) * 4294967296 + view.getUint32(off + 12, false);
        hdr = 16;
      } else if (size === 0) {
        size = end - off;
      }
      if (size < hdr) return;                                         // corrupt
      if (cb(type, off + hdr, Math.min(off + size, end)) === false) return;
      off += size;
    }
  }

  // HEIC/HEIF: ftyp brand check → meta → iinf (find the 'Exif' item id) →
  // iloc (find that item's extent). iloc versions 0–2; offset/length/base
  // sizes of 0, 4 or 8 bytes; construction_method must be 0 (file offsets).
  function findTiffInHeif(view) {
    var byteLen = view.byteLength;
    var brandOk = false, metaStart = -1, metaEnd = 0;
    eachBox(view, 0, byteLen, function (type, start, end) {
      if (type === 'ftyp') {
        for (var p = start; p + 4 <= end; p += 4) {
          if (p === start + 4) continue;                              // minor_version
          var b = fourcc(view, p);
          if (b === 'heic' || b === 'heix' || b === 'mif1' ||
              b === 'hevc' || b === 'heif' || b === 'msf1') brandOk = true;
        }
      } else if (type === 'meta') { metaStart = start + 4; metaEnd = end; } // fullbox
    }, 0);
    if (!brandOk || metaStart < 0) return -1;

    var exifId = null, ilocStart = -1, ilocEnd = 0;
    eachBox(view, metaStart, metaEnd, function (type, start, end) {
      if (type === 'iinf') {
        var v = view.getUint8(start);
        var p = start + 4 + (v === 0 ? 2 : 4);                        // skip entry_count
        eachBox(view, p, end, function (t2, s2) {
          if (t2 !== 'infe' || exifId !== null) return;
          var iv = view.getUint8(s2), q = s2 + 4;
          var id;
          if (iv >= 3) { id = view.getUint32(q, false); q += 4; }
          else { id = view.getUint16(q, false); q += 2; }
          q += 2;                                                     // item_protection_index
          if (fourcc(view, q) === 'Exif') exifId = id;
        }, 1);
      } else if (type === 'iloc') { ilocStart = start; ilocEnd = end; }
    }, 1);
    if (exifId === null || ilocStart < 0) return -1;

    var v = view.getUint8(ilocStart);
    var p = ilocStart + 4;
    var b0 = view.getUint8(p), b1 = view.getUint8(p + 1);
    var offSize = b0 >> 4, lenSize = b0 & 15;
    var baseSize = b1 >> 4;
    var idxSize = (v === 1 || v === 2) ? (b1 & 15) : 0;
    p += 2;
    var count;
    if (v < 2) { count = view.getUint16(p, false); p += 2; }
    else { count = view.getUint32(p, false); p += 4; }
    count = Math.min(count, MAX_BOXES);

    function readN(bytes) {                                           // big-endian 0/4/8-byte int
      var val = 0;
      for (var i = 0; i < bytes; i++) val = val * 256 + view.getUint8(p + i);
      p += bytes;
      return val;
    }

    for (var it = 0; it < count; it++) {
      if (p + 4 > ilocEnd) return -1;
      var id;
      if (v < 2) { id = view.getUint16(p, false); p += 2; }
      else { id = view.getUint32(p, false); p += 4; }
      var method = 0;
      if (v === 1 || v === 2) { method = view.getUint16(p, false) & 15; p += 2; }
      p += 2;                                                         // data_reference_index
      var base = readN(baseSize);
      var extents = view.getUint16(p, false); p += 2;
      var firstOff = null, firstLen = null;
      for (var x = 0; x < extents && x < MAX_BOXES; x++) {
        if (idxSize > 0) readN(idxSize);                              // extent_index (unused)
        var eo = readN(offSize), el = readN(lenSize);
        if (x === 0) { firstOff = eo; firstLen = el; }
      }
      if (id !== exifId) continue;
      if (method !== 0 || firstOff === null) return -1;               // idat/item refs unsupported
      var itemStart = base + firstOff;
      if (itemStart + 8 > byteLen) return -1;
      // Item payload: u32 exif_tiff_header_offset, then the Exif payload
      // (usually 'Exif\0\0' + TIFF, i.e. offset 6). Validate the magic and
      // rescan a small window if a writer anchored the offset differently.
      var t = itemStart + 4 + view.getUint32(itemStart, false);
      if (isTiffAt(view, t)) return t;
      for (var s = itemStart + 4; s < Math.min(itemStart + 64, byteLen - 8); s++) {
        if (isTiffAt(view, s)) return s;
      }
      return -1;
    }
    return -1;
  }

  // Public entry: sniff the container by magic bytes (never trust `mime` —
  // Windows often hands HEICs over with an empty type) and hand the TIFF
  // block to parseTiffBlock. Never throws.
  function exifParse(buf, mime, fileName) {
    try {
      var view;
      if (buf instanceof ArrayBuffer) view = new DataView(buf);
      else if (buf && buf.buffer instanceof ArrayBuffer) {
        view = new DataView(buf.buffer, buf.byteOffset || 0, buf.byteLength);
      } else return null;
      if (view.byteLength < 12) return null;

      var tiffAt = -1;
      var m16 = view.getUint16(0, false);
      if (m16 === 0xFFD8) tiffAt = findTiffInJpeg(view);
      else if (m16 === 0x8950) tiffAt = findTiffInPng(view);
      else if (fourcc(view, 0) === 'RIFF') tiffAt = findTiffInWebp(view);
      else if (isTiffAt(view, 0)) tiffAt = 0;
      else if (fourcc(view, 4) === 'ftyp') tiffAt = findTiffInHeif(view);
      if (tiffAt < 0) return null;
      return parseTiffBlock(view, tiffAt);
    } catch (err) {
      return null;
    }
  }

  /* ════════════════════════ 3 · place index (pure math + loader) ══════════
     Columnar format {v,count,n,la,lo,cc,p}; la/lo are deg×1000 ints, p is a
     log10-population bucket. Lookup: 1°×1° grid buckets, ring search, then
     haversine with a "prefer the bigger town when it's nearly as close"
     tie-break (within 1.4× of the nearest hit). */

  function normLonBucket(b) {
    // Wrap a longitude bucket into [-180, 179] so ring searches cross ±180.
    return ((b + 180) % 360 + 360) % 360 - 180;
  }

  function buildPlaceIndex(raw) {
    if (!raw || !raw.n || !raw.la || !raw.lo) return null;
    var buckets = new Map();
    for (var i = 0; i < raw.n.length; i++) {
      var key = Math.floor(raw.la[i] / 1000) + ':' + normLonBucket(Math.floor(raw.lo[i] / 1000));
      var arr = buckets.get(key);
      if (!arr) { arr = []; buckets.set(key, arr); }
      arr.push(i);
    }
    return { raw: raw, buckets: buckets };
  }

  function nearestPlace(idx, lat, lon) {
    if (!idx || typeof lat !== 'number' || typeof lon !== 'number' ||
        !isFinite(lat) || !isFinite(lon)) return null;
    var raw = idx.raw;
    var latB = Math.floor(lat), lonB = normLonBucket(Math.floor(lon));
    var cand = [];
    // 3×3 first (ring 1), expanding square rings up to 5 while empty.
    for (var ring = 1; ring <= 5 && cand.length === 0; ring++) {
      for (var dy = -ring; dy <= ring; dy++) {
        var lb = latB + dy;
        if (lb < -90 || lb > 89) continue;
        for (var dx = -ring; dx <= ring; dx++) {
          var arr = idx.buckets.get(lb + ':' + normLonBucket(lonB + dx));
          if (arr) for (var k = 0; k < arr.length; k++) cand.push(arr[k]);
        }
      }
    }
    if (!cand.length) return null;
    var items = [], bestKm = Infinity, i;
    for (i = 0; i < cand.length; i++) {
      var ci = cand[i];
      var km = haversineKm(lat, lon, raw.la[ci] / 1000, raw.lo[ci] / 1000);
      items.push({ i: ci, km: km });
      if (km < bestKm) bestKm = km;
    }
    // Prefer the highest population bucket among near-ties (≤ 1.4× nearest).
    var pick = null, limit = bestKm * 1.4 + 1e-9;
    for (i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.km > limit) continue;
      if (!pick || raw.p[it.i] > raw.p[pick.i] ||
          (raw.p[it.i] === raw.p[pick.i] && it.km < pick.km)) pick = it;
    }
    return { name: raw.n[pick.i], cc: raw.cc[pick.i], km: pick.km };
  }

  var _displayNames = null;
  function displayCountry(cc) {
    if (!cc) return null;
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
        if (!_displayNames) _displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        return _displayNames.of(cc) || cc;
      }
    } catch (e) { /* bad cc code */ }
    return cc;
  }

  var placeState = { idx: null, promise: null };

  function fetchPlacesJson(url) {
    // Cache API keeps the ~2 MB index out of repeat network fetches; fall
    // back to a plain fetch anywhere it's unavailable (http dev, private mode).
    function plain() {
      return fetch(url).then(function (r) {
        if (!r.ok) throw new Error('places fetch ' + r.status);
        return r.json();
      });
    }
    if (typeof caches === 'undefined' || !caches.open) return plain();
    return caches.open('kr-press-v1').then(function (cache) {
      return cache.match(url).then(function (hit) {
        if (hit) return hit.json();
        return fetch(url).then(function (res) {
          if (!res.ok) throw new Error('places fetch ' + res.status);
          try { cache.put(url, res.clone()).catch(function () {}); } catch (e) {}
          return res.json();
        });
      });
    }).catch(plain);
  }

  var places = {
    ensureLoaded: function () {
      if (placeState.promise) return placeState.promise;
      var url = KRPress.config && KRPress.config.assets && KRPress.config.assets.places;
      if (!url) {
        console.warn('[KRPress] no place-index URL in config');
        return Promise.resolve();
      }
      placeState.promise = fetchPlacesJson(url).then(function (raw) {
        placeState.idx = buildPlaceIndex(raw);
      }).catch(function (err) {
        console.warn('[KRPress] place index unavailable — photos will keep raw coordinates', err);
        placeState.promise = null;    // allow a retry on the next scan
      });
      return placeState.promise;
    },
    nearest: function (lat, lon) {
      return nearestPlace(placeState.idx, lat, lon);
    }
  };

  /* ════════════════════════ 4 · event bus ════════════════════════ */

  var _listeners = new Map();
  var bus = {
    on: function (evt, fn) {
      var set = _listeners.get(evt);
      if (!set) { set = new Set(); _listeners.set(evt, set); }
      set.add(fn);
    },
    off: function (evt, fn) {
      var set = _listeners.get(evt);
      if (set) set.delete(fn);
    },
    emit: function (evt, data) {
      var set = _listeners.get(evt);
      if (!set) return;
      // Copy first: handlers may subscribe/unsubscribe while we iterate.
      Array.from(set).forEach(function (fn) {
        try { fn(data); }
        catch (e) { console.error('[KRPress] bus handler for "' + evt + '" failed', e); }
      });
    }
  };

  /* ════════════════════════ 5 · storage (IndexedDB, memory fallback) ══════
     db 'kr-press' v1 — stores: project (key 'active'), blobs, thumbs, mids
     (all keyed by photoId). Any failure (private mode, quota, corruption)
     degrades to an in-memory Map so the session keeps working; we warn once
     and emit 'storage:degraded' so the UI can show its non-blocking toast. */

  var IDB_STORES = ['project', 'blobs', 'thumbs', 'mids'];

  var storage = (function () {
    var dbPromise = null;
    var mem = null;
    var degradedSent = false;

    function degrade(reason, err) {
      if (!mem) {
        mem = {};
        IDB_STORES.forEach(function (s) { mem[s] = new Map(); });
      }
      if (!degradedSent) {
        degradedSent = true;
        console.warn('[KRPress] persistent storage unavailable (' + reason +
                     ') — the draft lives in memory only for this session.', err || '');
        bus.emit('storage:degraded', { reason: reason });
      }
    }

    function open() {
      if (mem) return Promise.resolve(null);
      if (dbPromise) return dbPromise;
      dbPromise = new Promise(function (resolve) {
        var idb = (typeof indexedDB !== 'undefined') ? indexedDB : null;
        if (!idb) { degrade('unavailable'); resolve(null); return; }
        var req;
        try { req = idb.open('kr-press', 1); }
        catch (e) { degrade('open-throw', e); resolve(null); return; }
        req.onupgradeneeded = function () {
          var db = req.result;
          IDB_STORES.forEach(function (s) {
            if (!db.objectStoreNames.contains(s)) db.createObjectStore(s);
          });
        };
        req.onsuccess = function () {
          var db = req.result;
          db.onversionchange = function () { try { db.close(); } catch (e) {} };
          resolve(db);
        };
        req.onerror = function () { degrade('open-failed', req.error); resolve(null); };
        req.onblocked = function () { degrade('blocked'); resolve(null); };
      });
      return dbPromise;
    }

    function reqProm(req) {
      return new Promise(function (res, rej) {
        req.onsuccess = function () { res(req.result); };
        req.onerror = function () { rej(req.error); };
      });
    }

    return {
      get: function (store, key) {
        return open().then(function (db) {
          if (!db) return mem[store].get(key);
          return reqProm(db.transaction(store, 'readonly').objectStore(store).get(key));
        }).catch(function (e) { degrade('error', e); return mem[store].get(key); });
      },
      put: function (store, key, val) {
        return open().then(function (db) {
          if (!db) { mem[store].set(key, val); return; }
          return reqProm(db.transaction(store, 'readwrite').objectStore(store).put(val, key));
        }).catch(function (e) {
          // Quota overflow lands here too — keep the session alive in memory.
          degrade(e && e.name === 'QuotaExceededError' ? 'quota' : 'error', e);
          mem[store].set(key, val);
        });
      },
      del: function (store, key) {
        return open().then(function (db) {
          if (!db) { mem[store].delete(key); return; }
          return reqProm(db.transaction(store, 'readwrite').objectStore(store).delete(key));
        }).catch(function (e) { degrade('error', e); mem[store].delete(key); });
      },
      clear: function (store) {
        return open().then(function (db) {
          if (!db) { mem[store].clear(); return; }
          return reqProm(db.transaction(store, 'readwrite').objectStore(store).clear());
        }).catch(function (e) { degrade('error', e); mem[store].clear(); });
      },
      getAllPairs: function (store) {
        return open().then(function (db) {
          if (!db) return Array.from(mem[store].entries());
          var os = db.transaction(store, 'readonly').objectStore(store);
          return Promise.all([reqProm(os.getAllKeys()), reqProm(os.getAll())])
            .then(function (kv) {
              var out = [];
              for (var i = 0; i < kv[0].length; i++) out.push([kv[0][i], kv[1][i]]);
              return out;
            });
        }).catch(function (e) { degrade('error', e); return Array.from(mem[store].entries()); });
      }
    };
  })();

  /* ════════════════════════ 6 · object-URL cache (urls) ════════════════════
     Demo photos map straight to their remote S3 thumb; file photos get
     object URLs minted during ingest (and re-minted by store.load()). */

  var urlCache = { thumb: new Map(), mid: new Map(), full: new Map() };

  function setUrl(kind, id, url) {
    var old = urlCache[kind].get(id);
    if (old && old !== url && old.indexOf('blob:') === 0) {
      try { URL.revokeObjectURL(old); } catch (e) {}
    }
    urlCache[kind].set(id, url);
  }

  function revokeAllUrls() {
    ['thumb', 'mid', 'full'].forEach(function (kind) {
      urlCache[kind].forEach(function (u) {
        if (u && u.indexOf('blob:') === 0) { try { URL.revokeObjectURL(u); } catch (e) {} }
      });
      urlCache[kind].clear();
    });
  }

  function demoThumbUrl(slug) {
    var base = (KRPress.config && KRPress.config.demoBase) || '';
    return base.replace(/\/$/, '') + '/photos/thumb/' + slug + '.jpg';
  }

  var urls = {
    thumb: function (photoId) { return urlCache.thumb.get(photoId) || null; },
    mid: function (photoId) {
      // Studio canvas falls back to the thumb when no mid exists (demo
      // photos, EXIF-thumb-only previews).
      return urlCache.mid.get(photoId) || urlCache.thumb.get(photoId) || null;
    },
    full: function (photoId) {
      var hit = urlCache.full.get(photoId);
      if (hit) return Promise.resolve(hit);
      return storage.get('blobs', photoId).then(function (blob) {
        if (!blob) return urls.mid(photoId);        // demo photos have no original
        var u = URL.createObjectURL(blob);
        urlCache.full.set(photoId, u);
        return u;
      }).catch(function () { return urls.mid(photoId); });
    }
  };

  /* ════════════════════════ 7 · store (project + autosave) ════════════════ */

  function freshProject() {
    var now = Date.now();
    return {
      v: 1, id: uid('prj'), createdAt: now, updatedAt: now,
      title: '', subtitle: '',
      format: 'hardcover', photosPerPage: 6,
      photos: [], book: null, pages: null, edits: {}
    };
  }

  function saveProjectNow() {
    if (!store.project) return Promise.resolve();
    // Persist a STRUCTURED CLONE — never live references the UI could keep
    // mutating while the transaction is in flight.
    var snapshot;
    try { snapshot = deepClone(store.project); }
    catch (e) { console.warn('[KRPress] could not snapshot project', e); return Promise.resolve(); }
    return storage.put('project', 'active', snapshot).catch(function (e) {
      console.warn('[KRPress] draft save failed', e);
    });
  }

  var scheduleSave = debounce(saveProjectNow, 800);

  /* Flush the debounced autosave when the page is hidden or unloaded, so
     edits made inside the 800 ms window aren't lost to navigation or a
     closed tab. flush() is a no-op when nothing is pending, and
     saveProjectNow() no-ops without a project. Guarded for the Node test
     shim (bare `global.window`, no document). */
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pagehide', function () { scheduleSave.flush(); });
  }
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') scheduleSave.flush();
    });
  }

  function hydrateUrls(project) {
    // Rebuild the in-memory URL caches after a reload so urls.thumb()/mid()
    // are synchronously available the moment load() resolves.
    (project.photos || []).forEach(function (m) {
      if (m.source === 'demo' && m.demoSlug) {
        var u = demoThumbUrl(m.demoSlug);
        setUrl('thumb', m.id, u);
        setUrl('mid', m.id, u);
      }
    });
    if (typeof URL === 'undefined' || !URL.createObjectURL) return Promise.resolve();
    return Promise.all([
      storage.getAllPairs('thumbs').then(function (pairs) {
        pairs.forEach(function (kv) { if (kv[1]) setUrl('thumb', kv[0], URL.createObjectURL(kv[1])); });
      }),
      storage.getAllPairs('mids').then(function (pairs) {
        pairs.forEach(function (kv) { if (kv[1]) setUrl('mid', kv[0], URL.createObjectURL(kv[1])); });
      })
    ]).catch(function () {});
  }

  var store = {
    project: null,

    hasSaved: function () {
      return storage.get('project', 'active')
        .then(function (p) { return !!p; })
        .catch(function () { return false; });
    },

    load: function () {
      // Once hydrated, the in-memory project is the source of truth — the
      // IDB copy can lag behind by the autosave debounce (800 ms), so
      // re-reading it here could resurrect stale state (e.g. a stage the
      // user just left).
      if (store.project) return Promise.resolve(store.project);
      return storage.get('project', 'active').then(function (p) {
        if (!p) return null;
        store.project = p;
        return hydrateUrls(p).then(function () { return p; });
      }).catch(function () { return null; });
    },

    create: function () {
      store.project = freshProject();
      scheduleSave();
      return store.project;
    },

    update: function (fn) {
      if (!store.project) {
        // Callers create projects explicitly (store.create / ingest); auto-
        // creating here could resurrect state from a queue that survived
        // reset() — warn and drop instead.
        console.warn('[KRPress] store.update ignored — no active project');
        return;
      }
      try { fn(store.project); }
      catch (e) { console.error('[KRPress] store.update mutator failed', e); }
      store.project.updatedAt = Date.now();
      scheduleSave();
      bus.emit('project-changed', store.project);
    },

    // Part of the public store contract (ARCHITECTURE §Core public API):
    // cancel any pending debounced autosave and write the project to storage
    // immediately. Shells call this before navigating away (e.g. add-to-cart
    // → /cart) so the IDB write commits during the network round-trip.
    saveNow: function () {
      scheduleSave.cancel();
      return saveProjectNow();
    },

    reset: function () {
      ingestGen++;                      // abandon any in-flight scan queue
      scheduleSave.cancel();
      store.project = null;
      revokeAllUrls();
      return Promise.all(IDB_STORES.map(function (s) { return storage.clear(s); }))
        .then(function () {});
    }
  };

  /* ════════════════════════ 8 · thumbnailer ════════════════════════
     thumb = 320 px @ q0.82 — filmstrip/tray/frames-grid cells, always
     small on the page, no reason to store more.
     mid   = 2200 px @ q0.87 — every hero/full-bleed photo the studio
     shows large. At 2200 px a full-bleed image (~90% of an A4-landscape
     spread) stays sharp up to ~2x device pixel ratio without upscaling —
     the whole point being the preview should visibly promise "your print
     won't be blurry", not just gesture at the photo. Still well short of
     print DPI (A4 @ 300dpi ≈ 3500 px) — the ORIGINAL FILE (kept in the
     'blobs' store, served via urls.full()) is the true source for print
     and for the on-zoom upgrade in the desktop stage (kr-press-builder.js
     applyStageTransform → upgradeSheetImages: thumbs step up to this mid
     as zoom starts, mids step up to the original).
     Decode ladder: createImageBitmap({imageOrientation:'from-image'}) →
     createImageBitmap plain → <img> decode → EXIF-embedded thumbnail
     (rotated manually — embedded thumbs carry no EXIF of their own, so
     'from-image' can't help) → nulls (UI renders a placeholder tile). */

  var THUMB_EDGE = 320, MID_EDGE = 2200, JPEG_QUALITY = 0.82, MID_QUALITY = 0.87;

  function makeCanvas(w, h) {
    if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
  }

  function canvasToJpeg(canvas, quality) {
    quality = quality == null ? JPEG_QUALITY : quality;
    return new Promise(function (resolve) {
      if (canvas.convertToBlob) {
        canvas.convertToBlob({ type: 'image/jpeg', quality: quality })
          .then(resolve, function () { resolve(null); });
        return;
      }
      try { canvas.toBlob(function (b) { resolve(b || null); }, 'image/jpeg', quality); }
      catch (e) { resolve(null); }
    });
  }

  function srcSize(src) {
    return { w: src.width || src.naturalWidth || 0, h: src.height || src.naturalHeight || 0 };
  }

  function scaleToJpeg(src, edge, quality) {
    var s = srcSize(src);
    if (!s.w || !s.h) return Promise.resolve(null);
    var f = Math.min(1, edge / Math.max(s.w, s.h));
    var w = Math.max(1, Math.round(s.w * f)), h = Math.max(1, Math.round(s.h * f));
    var c = makeCanvas(w, h);
    var ctx = c.getContext('2d');
    ctx.drawImage(src, 0, 0, w, h);
    return canvasToJpeg(c, quality);
  }

  // Manual EXIF orientation for sources that were decoded without it
  // (embedded thumbnails). Canvas dims swap for orientations 5–8.
  function orientedJpeg(src, orientation, edge) {
    var s = srcSize(src);
    if (!s.w || !s.h) return Promise.resolve(null);
    var f = Math.min(1, edge / Math.max(s.w, s.h));
    var w = Math.max(1, Math.round(s.w * f)), h = Math.max(1, Math.round(s.h * f));
    var swap = orientation >= 5 && orientation <= 8;
    var c = makeCanvas(swap ? h : w, swap ? w : h);
    var ctx = c.getContext('2d');
    switch (orientation) {
      case 2: ctx.setTransform(-1, 0, 0, 1, w, 0); break;   // mirror X
      case 3: ctx.setTransform(-1, 0, 0, -1, w, h); break;  // 180°
      case 4: ctx.setTransform(1, 0, 0, -1, 0, h); break;   // mirror Y
      case 5: ctx.setTransform(0, 1, 1, 0, 0, 0); break;    // transpose
      case 6: ctx.setTransform(0, 1, -1, 0, h, 0); break;   // 90° CW
      case 7: ctx.setTransform(0, -1, -1, 0, h, w); break;  // transverse
      case 8: ctx.setTransform(0, -1, 1, 0, 0, w); break;   // 90° CCW
      default: break;
    }
    ctx.drawImage(src, 0, 0, w, h);
    return canvasToJpeg(c);
  }

  function decodeViaImg(blob) {
    return new Promise(function (resolve, reject) {
      if (typeof Image === 'undefined' || typeof URL === 'undefined') {
        reject(new Error('no Image decode available'));
        return;
      }
      var u = URL.createObjectURL(blob);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(u); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(u); reject(new Error('img decode failed')); };
      img.src = u;
    });
  }

  function decodeBitmap(blob) {
    if (typeof createImageBitmap === 'function') {
      var p;
      // Older engines throw synchronously on the options bag.
      try { p = createImageBitmap(blob, { imageOrientation: 'from-image' }); }
      catch (e) { p = Promise.reject(e); }
      return p
        .catch(function () { return createImageBitmap(blob); })
        .catch(function () { return decodeViaImg(blob); })
        .catch(function () { return null; });
    }
    return decodeViaImg(blob).catch(function () { return null; });
  }

  function makePreviews(file, exif) {
    var none = { thumb: null, mid: null };
    if (typeof document === 'undefined' && typeof OffscreenCanvas === 'undefined') {
      return Promise.resolve(none);
    }
    return decodeBitmap(file).then(function (src) {
      if (src) {
        return scaleToJpeg(src, MID_EDGE, MID_QUALITY).then(function (mid) {
          return scaleToJpeg(src, THUMB_EDGE).then(function (thumb) {
            if (src.close) { try { src.close(); } catch (e) {} }
            return { thumb: thumb, mid: mid };
          });
        });
      }
      // Full decode failed (HEIC on Chrome/Firefox, RAW): fall back to the
      // EXIF-embedded JPEG thumbnail, applying orientation ourselves.
      if (exif && exif.thumb && exif.thumb.length) {
        var tblob = new Blob([exif.thumb], { type: 'image/jpeg' });
        return decodeBitmap(tblob).then(function (tsrc) {
          if (!tsrc) return none;
          return orientedJpeg(tsrc, exif.orientation || 1, THUMB_EDGE).then(function (thumb) {
            if (tsrc.close) { try { tsrc.close(); } catch (e) {} }
            // Store the same small preview as the mid so the studio canvas
            // has *something* — better than a hole in the layout.
            return { thumb: thumb, mid: thumb };
          });
        });
      }
      return none;
    }).catch(function () { return none; });
  }

  /* ════════════════════════ 9 · filename heuristics (pure) ════════════════ */

  function missingWhy(name) {
    if (/^IMG-\d{8}-WA\d+/i.test(name || '')) return 'WHATSAPP EXPORT';
    if (/^screen[\s_-]?shot/i.test(name || '')) return 'SCREENSHOT';
    if (/edit/i.test(name || '') || /\(\d+\)/.test(name || '')) return 'RE-EXPORTED COPY';
    return 'NO METADATA';
  }

  // Extract a candidate wall-clock timestamp embedded in a filename.
  // precision 'time' → trustworthy to the second; 'day' → date only
  // (e.g. WhatsApp's IMG-20250821-WA0012). Returns null when nothing sane.
  function filenameDate(name) {
    if (!name) return null;
    var y, mo, d, h = 12, mi = 0, s = 0, precision = 'day', m;
    // Screenshot_2025-08-19-104233 / Screenshot_2025-08-08-2117
    m = /(\d{4})-(\d{2})-(\d{2})[-_ ](\d{2})[.:_-]?(\d{2})[.:_-]?(\d{2})?/.exec(name);
    if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; h = +m[4]; mi = +m[5]; s = +(m[6] || 0); precision = 'time'; }
    else {
      // PXL_20250819_104233 / IMG_20250819_104233 — compact date + time
      m = /(20\d{2})(\d{2})(\d{2})[-_](\d{2})(\d{2})(\d{2})/.exec(name);
      if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; h = +m[4]; mi = +m[5]; s = +m[6]; precision = 'time'; }
      else {
        // IMG-20250821-WA0012 — compact date only
        m = /(20\d{2})(\d{2})(\d{2})/.exec(name);
        if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; }
        else {
          // bare dashed date
          m = /(\d{4})-(\d{2})-(\d{2})/.exec(name);
          if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; }
          else return null;
        }
      }
    }
    if (y < 1990 || y > 2100 || mo < 1 || mo > 12 || d < 1 || d > 31 ||
        h > 23 || mi > 59 || s > 60) return null;
    return {
      t: new Date(y, mo - 1, d, h, mi, s).getTime(),   // wall clock as local
      precision: precision,
      wall: { y: y, mo: mo, d: d, h: h, mi: mi, s: s }
    };
  }

  function sameWallDay(meta, wall) {
    var w = wallOf(meta);
    return !!(w && wall && w.y === wall.y && w.mo === wall.mo && w.d === wall.d);
  }

  // Synthesize slot hints for 'missing' photos: neighbour-window suggestion
  // per ARCHITECTURE §Photo ingest step 5. Time source priority:
  //   filename time (exact) → filename day (midpoint of the tightest
  //   same-day rich pair — the "between IMG_4471 and IMG_4473, 28 minutes
  //   apart" rule) → file lastModified (bracket only) → ingest order.
  function synthesizeSlotHints(project, lastModByPhotoId) {
    var photos = (project && project.photos) || [];
    var rich = photos.filter(function (m) {
      return m.status === 'rich' && typeof m.takenAt === 'number';
    }).sort(function (a, b) { return a.takenAt - b.takenAt; });

    photos.forEach(function (m) {
      if (m.status !== 'missing' || m.slotHint) return;
      var hint = hintForMissing(m, photos, rich, lastModByPhotoId);
      if (hint) m.slotHint = hint;
    });
  }

  function bracket(richSorted, t) {
    var prev = null, next = null;
    for (var i = 0; i < richSorted.length; i++) {
      if (richSorted[i].takenAt <= t) prev = richSorted[i];
      else { next = richSorted[i]; break; }
    }
    return { prev: prev, next: next };
  }

  // Wall clock at instant `t`, expressed in the ANCHOR photo's time frame
  // (never the viewer's zone — display rule #7). We shift the anchor's wall
  // components by (t − anchor.takenAt) in a neutral UTC frame and read the
  // components back, which is exact for any fixed offset.
  function wallAtInstant(anchor, t) {
    var w = anchor && wallOf(anchor);
    if (!w || typeof anchor.takenAt !== 'number') return wallOf({ takenAt: t });
    var d = new Date(Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s) + (t - anchor.takenAt));
    return { y: d.getUTCFullYear(), mo: d.getUTCMonth() + 1, d: d.getUTCDate(),
             h: d.getUTCHours(), mi: d.getUTCMinutes(), s: d.getUTCSeconds() };
  }

  function hintForMissing(m, photos, richSorted, lastModByPhotoId) {
    if (!richSorted.length) return null;   // nothing to anchor against
    var cand = filenameDate(m.fileName);
    var lastMod = lastModByPhotoId && lastModByPhotoId.get ? lastModByPhotoId.get(m.id) : null;
    var prev = null, next = null, t = null, dateWall = null;

    if (cand && cand.precision === 'time') {
      // Filename carries a full timestamp — trust it, bracket for place only.
      t = cand.t;
      dateWall = cand.wall;
      var br = bracket(richSorted, t);
      prev = br.prev; next = br.next;
    } else if (cand && cand.precision === 'day') {
      dateWall = cand.wall;
      var sameDay = richSorted.filter(function (r) { return sameWallDay(r, cand.wall); });
      if (sameDay.length >= 2) {
        // The tightest same-day pair is the most likely capture window.
        var bi = 0, bg = Infinity;
        for (var i = 0; i + 1 < sameDay.length; i++) {
          var g = sameDay[i + 1].takenAt - sameDay[i].takenAt;
          if (g < bg) { bg = g; bi = i; }
        }
        prev = sameDay[bi]; next = sameDay[bi + 1];
        t = Math.round((prev.takenAt + next.takenAt) / 2);
      } else if (sameDay.length === 1) {
        prev = sameDay[0];
        t = prev.takenAt;
      } else {
        var br2 = bracket(richSorted, cand.t);
        prev = br2.prev; next = br2.next;
        t = (prev && next) ? Math.round((prev.takenAt + next.takenAt) / 2)
                           : (prev || next).takenAt;
      }
    } else if (typeof lastMod === 'number' && lastMod > 0) {
      var br3 = bracket(richSorted, lastMod);
      prev = br3.prev; next = br3.next;
      t = (prev && next) ? Math.round((prev.takenAt + next.takenAt) / 2)
                         : (prev || next).takenAt;
    } else {
      // No time signal at all — nearest rich neighbours by ingest order.
      var idx = photos.indexOf(m);
      for (var a = idx - 1; a >= 0; a--) {
        if (photos[a].status === 'rich' && typeof photos[a].takenAt === 'number') { prev = photos[a]; break; }
      }
      for (var b = idx + 1; b < photos.length; b++) {
        if (photos[b].status === 'rich' && typeof photos[b].takenAt === 'number') { next = photos[b]; break; }
      }
      if (!prev && !next) return null;
      t = (prev && next) ? Math.round((prev.takenAt + next.takenAt) / 2)
                         : (prev || next).takenAt;
    }

    if (t === null) return null;
    var anchor = prev || next;
    var place = (prev && next && prev.place === next.place)
      ? prev.place
      : (anchor && anchor.place) || null;
    // Labels in the photos' own wall-clock frame: a filename timestamp IS a
    // wall clock already; otherwise project the instant through the anchor's
    // frame so '≈2:20 PM' stays 2:20 PM whatever zone the viewer is in.
    var labelWall = (cand && cand.precision === 'time') ? cand.wall
                  : wallAtInstant(anchor, t);
    return {
      place: place,
      dateLabel: fmtDay({ wall: dateWall || labelWall }),
      timeLabel: '≈' + fmtTime({ wall: labelWall }),   // '≈2:20 PM' per design copy
      takenAt: t,
      // Display frame for consumers (engine wallOf prefers .wall): keeps a
      // 'fixed' photo's date/time from shifting with the viewer's zone.
      wall: labelWall,
      tzOffsetMin: (anchor && typeof anchor.tzOffsetMin === 'number') ? anchor.tzOffsetMin : null
    };
  }

  /* ════════════════════════ 10 · ingest pipeline ════════════════════════ */

  var OK_MIME = /^image\/(jpeg|jpg|png|webp|heic|heif|tiff?)$/i;
  var OK_EXT = /\.(jpe?g|png|webp|heic|heif|tiff?|dng|raf|cr2|cr3|nef|arw)$/i;
  var MIME_BY_EXT = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
    heic: 'image/heic', heif: 'image/heif', tif: 'image/tiff', tiff: 'image/tiff',
    dng: 'image/x-adobe-dng', raf: 'image/x-fuji-raf', cr2: 'image/x-canon-cr2',
    cr3: 'image/x-canon-cr3', nef: 'image/x-nikon-nef', arw: 'image/x-sony-arw'
  };

  function mimeFromName(name) {
    var m = /\.([a-z0-9]+)$/i.exec(name || '');
    return (m && MIME_BY_EXT[m[1].toLowerCase()]) || 'application/octet-stream';
  }

  function readArrayBuffer(file) {
    if (file.arrayBuffer) return file.arrayBuffer();
    return new Promise(function (resolve, reject) {
      var fr = new FileReader();
      fr.onload = function () { resolve(fr.result); };
      fr.onerror = function () { reject(fr.error); };
      fr.readAsArrayBuffer(file);
    });
  }

  function buildMeta(id, file, ex) {
    var hasTime = !!(ex && typeof ex.takenAt === 'number');
    var hasGps = !!(ex && ex.lat !== null && ex.lat !== undefined &&
                    ex.lon !== null && ex.lon !== undefined);
    var rich = hasTime && hasGps;   // both GPS + time present ⇒ rich
    return {
      id: id,
      fileName: file.name || 'photo',
      mime: file.type || mimeFromName(file.name),
      bytes: file.size || 0,
      w: (ex && ex.w) || null,
      h: (ex && ex.h) || null,
      takenAt: hasTime ? ex.takenAt : null,
      tzOffsetMin: (ex && ex.tzOffsetMin !== undefined) ? ex.tzOffsetMin : null,
      wall: (ex && ex.wall) || null,
      lat: hasGps ? ex.lat : null,
      lon: hasGps ? ex.lon : null,
      alt: (ex && ex.alt !== null && ex.alt !== undefined) ? ex.alt : null,
      camera: (ex && ex.camera) || null,
      orientation: (ex && ex.orientation) || 1,
      place: null, placeCC: null,
      status: rich ? 'rich' : 'missing',
      missingWhy: rich ? null : missingWhy(file.name || ''),
      resolution: null,
      slotHint: null,
      source: 'file'
    };
  }

  function ingestOne(file, index, total, batch, lastModById, gen) {
    return readArrayBuffer(file).then(function (buf) {
      var ex = null;
      try { ex = exifParse(buf, file.type, file.name); } catch (e) { /* never throws, but belt+braces */ }
      return makePreviews(file, ex).then(function (prev) {
        if (gen !== ingestGen) return;    // reset() while decoding — drop this file
        var id = uid('ph');
        var meta = buildMeta(id, file, ex);

        // Offline place naming: ≤120 km ⇒ nearest city; beyond ⇒ country.
        if (meta.lat !== null && meta.lon !== null) {
          var near = places.nearest(meta.lat, meta.lon);
          if (near) {
            meta.place = near.km <= 120 ? near.name : (displayCountry(near.cc) || near.cc);
            meta.placeCC = near.cc;
          }
        }

        var writes = [storage.put('blobs', id, file)];
        if (prev.thumb) {
          writes.push(storage.put('thumbs', id, prev.thumb));
          if (typeof URL !== 'undefined' && URL.createObjectURL) setUrl('thumb', id, URL.createObjectURL(prev.thumb));
        }
        if (prev.mid) {
          writes.push(storage.put('mids', id, prev.mid));
          if (typeof URL !== 'undefined' && URL.createObjectURL) setUrl('mid', id, URL.createObjectURL(prev.mid));
        }
        return Promise.all(writes).catch(function () {}).then(function () {
          if (gen !== ingestGen) return;  // reset() during the writes — don't
                                          // push into the new (or absent) project
          lastModById.set(id, file.lastModified || null);
          batch.push(meta);
          store.update(function (p) { p.photos.push(meta); });
          bus.emit('scan:photo', { meta: meta, index: index, total: total });
        });
      });
    }).catch(function (e) {
      console.warn('[KRPress] could not read "' + (file.name || 'file') + '"', e);
      bus.emit('scan:error', { fileName: file.name || 'file', reason: 'COULD NOT READ FILE' });
    });
  }

  /* Bumped by store.reset(): any scan queue started under an older
     generation quietly stops instead of pushing photos into the new
     (or absent) project. `var` hoists to the IIFE scope, so the store
     above sees it. */
  var ingestGen = 0;

  function addFiles(fileListOrArray) {
    var files = Array.prototype.slice.call(fileListOrArray || []);
    if (!files.length) return Promise.resolve();
    if (!store.project) store.create();
    var gen = ingestGen;

    var cfg = KRPress.config || {};
    var maxPhotos = cfg.maxPhotos || 500;
    var maxBytes = (cfg.maxTotalMb || 2048) * 1048576;
    var have = store.project.photos.length;
    var haveBytes = store.project.photos.reduce(function (s, m) { return s + (m.bytes || 0); }, 0);

    var accepted = [], rejected = [];
    files.forEach(function (f) {
      var name = f.name || 'photo';
      if (!(OK_MIME.test(f.type || '') || OK_EXT.test(name))) {
        rejected.push({ fileName: name, reason: 'UNSUPPORTED FILE TYPE' });
        return;
      }
      if (have + accepted.length >= maxPhotos) {
        rejected.push({ fileName: name, reason: 'OVER THE ' + maxPhotos + '-PHOTO LIMIT' });
        return;
      }
      if (haveBytes + (f.size || 0) > maxBytes) {
        rejected.push({ fileName: name, reason: 'OVER THE ' + (cfg.maxTotalMb || 2048) + ' MB TOTAL LIMIT' });
        return;
      }
      haveBytes += f.size || 0;
      accepted.push(f);
    });

    // Every file failed the gate: reject WITHOUT starting a scan so the UI
    // can stay on the landing stage and show a targeted error instead of
    // advancing to an absurd "all 0 photos" report.
    if (!accepted.length && rejected.length) {
      var noneErr = new Error('NO_SUPPORTED_FILES');
      noneErr.code = 'NO_SUPPORTED_FILES';
      return Promise.reject(noneErr);
    }

    var total = accepted.length;
    bus.emit('scan:start', { total: total });
    // Gate rejections surface AFTER scan:start — listeners build their scan
    // state on scan:start and would drop errors emitted before it.
    rejected.forEach(function (r) { bus.emit('scan:error', r); });
    if (!total) {                         // unreachable today (every file is
      bus.emit('scan:done', { rich: 0, missing: 0, total: 0 });   // accepted or
      return Promise.resolve();           // rejected) — belt+braces
    }

    var batch = [];                       // metas added in this call
    var lastModById = new Map();          // file.lastModified per photo id
    // Sequential queue (await each) so scan events stream one photo at a
    // time and the UI animates from REAL progress.
    var chain = places.ensureLoaded().catch(function () {});
    accepted.forEach(function (f, i) {
      chain = chain.then(function () {
        if (gen !== ingestGen) return;              // reset() mid-scan — stop
        return ingestOne(f, i, total, batch, lastModById, gen);
      });
    });

    return chain.then(function () {
      if (gen !== ingestGen) return;
      store.update(function (p) { synthesizeSlotHints(p, lastModById); });
      var rich = 0, missing = 0;
      batch.forEach(function (m) { if (m.status === 'rich') rich++; else missing++; });
      bus.emit('scan:done', { rich: rich, missing: missing, total: total });
    });
  }

  /* Demo datasets carry ISO timestamps in the trip's own zone ("…+07:00").
     Keep that zone as the wall clock — same rule as EXIF OffsetTimeOriginal —
     rather than letting Date.parse shift it into the viewer's zone. */
  function demoTime(v) {
    if (typeof v === 'number') return { takenAt: v, tzOffsetMin: null, wall: null };
    if (typeof v !== 'string') return null;
    var m = v.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/);
    if (!m) { var p = Date.parse(v); return isNaN(p) ? null : { takenAt: p, tzOffsetMin: null, wall: null }; }
    var wall = { y: +m[1], mo: +m[2], d: +m[3], h: +m[4], mi: +m[5], s: +(m[6] || 0) };
    var tz = m[7], tzMin = null;
    if (tz === 'Z') tzMin = 0;
    else if (tz) tzMin = (tz.charAt(0) === '-' ? -1 : 1) * (parseInt(tz.slice(1, 3), 10) * 60 + parseInt(tz.slice(-2), 10));
    var takenAt = (tzMin === null)
      ? new Date(wall.y, wall.mo - 1, wall.d, wall.h, wall.mi, wall.s).getTime()
      : Date.UTC(wall.y, wall.mo - 1, wall.d, wall.h, wall.mi, wall.s) - tzMin * 60000;
    return { takenAt: takenAt, tzOffsetMin: tzMin, wall: wall };
  }

  // slotHint.takenAt may arrive as ISO from demo datasets — normalize to ms,
  // keeping the ISO string's own zone as the hint's display frame (wall +
  // tzOffsetMin), same rule as EXIF OffsetTimeOriginal.
  function normSlotHint(hint) {
    if (!hint) return null;
    if (typeof hint.takenAt === 'string') {
      var t = demoTime(hint.takenAt);
      var out = {};
      for (var k in hint) out[k] = hint[k];
      out.takenAt = t ? t.takenAt : null;
      if (t) {
        if (out.wall == null) out.wall = t.wall;
        if (out.tzOffsetMin == null) out.tzOffsetMin = t.tzOffsetMin;
      }
      return out;
    }
    return hint;
  }

  // Camera synthesis for demo metas so the "CAMERAS SEEN" stat has real data.
  function demoCamera(fileName) {
    if (/^DSCF/i.test(fileName || '')) return 'Fujifilm X-T4';
    if (/^IMG_\d+\.heic$/i.test(fileName || '')) return 'Apple iPhone 15 Pro';
    return null;
  }

  function addDemo(demoPhotos) {
    var list = (demoPhotos || []).slice();
    if (!list.length) return Promise.resolve();
    if (!store.project) store.create();
    var gen = ingestGen;

    var total = list.length;
    bus.emit('scan:start', { total: total });

    var rich = 0, missing = 0;
    var chain = places.ensureLoaded().catch(function () {});
    list.forEach(function (d, i) {
      chain = chain.then(function () {
        if (gen !== ingestGen) return;              // reset() mid-scan — stop
        // 55 ms per photo — the design's "quick" scan cadence, so the scan
        // stage remains legible when photos need no actual parsing. Pacing is
        // cosmetic: skip it while the tab is hidden — Chrome intensively
        // throttles chained timers in hidden tabs (~1/minute), which would
        // stall the queue until the user returns.
        var paced = (typeof document !== 'undefined' && document.visibilityState === 'hidden')
          ? Promise.resolve() : delay(55);
        return paced.then(function () {
          if (gen !== ingestGen) return;
          var id = uid('ph');
          var thumbUrl = demoThumbUrl(d.slug);
          setUrl('thumb', id, thumbUrl);
          setUrl('mid', id, thumbUrl);

          var isMissing = !!d.missing;
          var near = (!isMissing && typeof d.lat === 'number' && typeof d.lon === 'number')
            ? places.nearest(d.lat, d.lon) : null;
          var dt = isMissing ? null : demoTime(d.takenAt);

          var meta = {
            id: id,
            fileName: d.fileName,
            mime: mimeFromName(d.fileName),
            bytes: 0, w: null, h: null,
            takenAt: dt ? dt.takenAt : null,
            tzOffsetMin: dt ? dt.tzOffsetMin : null,
            wall: dt ? dt.wall : null,
            lat: isMissing ? null : (typeof d.lat === 'number' ? d.lat : null),
            lon: isMissing ? null : (typeof d.lon === 'number' ? d.lon : null),
            alt: null,
            camera: demoCamera(d.fileName),
            orientation: 1,
            place: isMissing ? null : (d.place || (near && near.name) || null),
            placeCC: near ? near.cc : null,
            status: isMissing ? 'missing' : 'rich',
            missingWhy: isMissing ? (d.missing.why || missingWhy(d.fileName)) : null,
            missingCtx: isMissing ? (d.missing.ctx || null) : null,
            resolution: null,
            slotHint: isMissing ? normSlotHint(d.missing.slotHint) : null,
            source: 'demo',
            demoSlug: d.slug
          };
          if (isMissing) missing++; else rich++;
          store.update(function (p) { p.photos.push(meta); });
          bus.emit('scan:photo', { meta: meta, index: i, total: total });
        });
      });
    });

    return chain.then(function () {
      if (gen !== ingestGen) return;
      // Fill any demo 'missing' entries that came without a pre-built hint.
      store.update(function (p) { synthesizeSlotHints(p, null); });
      bus.emit('scan:done', { rich: rich, missing: missing, total: total });
    });
  }

  var ingest = { addFiles: addFiles, addDemo: addDemo };

  /* ════════════════════════ 11 · module registry + boot router ════════════ */

  var registry = {};
  var current = null;   // { name, module }

  function register(name, module) {
    if (!name || !module || typeof module.mount !== 'function') {
      console.error('[KRPress] register(name, {mount, unmount}) — bad module for "' + name + '"');
      return;
    }
    registry[name] = module;
  }

  function moduleCtx() {
    return {
      config: KRPress.config,
      store: store,
      engine: (typeof window !== 'undefined' && window.KRPressEngine) || null,
      bus: bus,
      urls: urls
    };
  }

  function mountModule(name) {
    var root = document.getElementById('krp-root');
    if (!root) {
      console.error('[KRPress] #krp-root missing — cannot mount "' + name + '"');
      return false;
    }
    if (current && current.name === name) return true;   // already mounted
    var mod = registry[name];
    if (!mod) {
      // Leave the .krp-boot splash in place so the page never goes blank.
      console.error('[KRPress] no module registered for surface "' + name + '" — leaving boot splash');
      return false;
    }
    if (current && current.module.unmount) {
      try { current.module.unmount(); }
      catch (e) { console.error('[KRPress] "' + current.name + '".unmount() failed', e); }
    }
    while (root.firstChild) root.removeChild(root.firstChild);   // clears splash too
    current = { name: name, module: mod };
    try { mod.mount(root, moduleCtx()); }
    catch (e) { console.error('[KRPress] "' + name + '".mount() failed', e); }
    return true;
  }

  function isMobile() {
    try {
      if (window.matchMedia('(max-width: 760px)').matches) return true;
      if (window.matchMedia('(pointer: coarse)').matches && window.innerWidth < 900) return true;
    } catch (e) { /* matchMedia unavailable */ }
    return false;
  }

  var booted = false;
  function boot() {
    if (booted) return;
    booted = true;

    // 1 · config
    var el = document.getElementById('krp-config');
    if (el) {
      try { KRPress.config = JSON.parse(el.textContent); }
      catch (e) { console.error('[KRPress] #krp-config is not valid JSON', e); }
    } else {
      console.error('[KRPress] #krp-config missing');
    }

    // 2 · surface routing. Onboarding owns landing→reveal on BOTH surfaces
    // (the mobile studio has no import flow of its own — spec-mobile §9) and
    // hands off via 'open-studio', which picks the studio shell by surface.
    bus.on('open-studio', function () { mountModule(isMobile() ? 'mobile' : 'builder'); });
    bus.on('restart', function () { mountModule('onboarding'); });

    // 3 · mount the entry surface (replaces the .krp-boot splash; if the
    // module never registered, the splash stays and we log the error).
    // A returning visitor with a composed draft goes straight to the studio.
    store.hasSaved().then(function (has) {
      if (!has) { mountModule('onboarding'); return; }
      return store.load().then(function (p) {
        if (p && p.stage === 'studio' && p.photos && p.photos.length) {
          mountModule(isMobile() ? 'mobile' : 'builder');
        } else {
          mountModule('onboarding');
        }
      });
    }).catch(function () { mountModule('onboarding'); });
  }

  /* ════════════════════════ 12 · public namespace ════════════════════════ */

  var KRPress = {
    config: null,
    bus: bus,
    register: register,
    store: store,
    ingest: ingest,
    urls: urls,
    places: places,
    exif: { parse: exifParse },
    util: {
      haversineKm: haversineKm,
      fmtDay: fmtDay,
      fmtTime: fmtTime,
      fmtDateRange: fmtDateRange,
      debounce: debounce,
      uid: uid,
      escapeHtml: escapeHtml
    },
    boot: boot,

    // Internal, non-contract hooks for the Node test harness. UI modules
    // must not touch these.
    _internal: {
      parseTiffBlock: parseTiffBlock,
      buildPlaceIndex: buildPlaceIndex,
      nearestPlace: nearestPlace,
      filenameDate: filenameDate,
      missingWhy: missingWhy,
      synthesizeSlotHints: synthesizeSlotHints,
      wallOf: wallOf
    }
  };

  if (typeof window !== 'undefined') window.KRPress = KRPress;

  // Script is loaded with `defer`, so DOMContentLoaded has not fired yet in
  // the normal flow; the extra readyState/load guards cover late injection.
  if (typeof document !== 'undefined') {
    if (document.readyState === 'complete') boot();
    else {
      document.addEventListener('DOMContentLoaded', boot);
      window.addEventListener('load', boot);   // fallback if injected post-DCL
    }
  }
})();
